import {
  ID,
  IDPrefixes,
  PrecomputedCompletion,
  rootId,
  ScheduleEntryState,
  SortingMode,
  Task,
  TaskCheckOptions,
  TaskListItemTypes,
  TaskListTreeItem,
  TaskType,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { startOfDay } from 'date-fns';

import {
  createCompositeId,
  getCompositeIdParts,
} from '../encoded-ids/composite-id';
import { formatDateKey, parseDateKey } from '../format-date-key';
import { getScheduleFrequencyDayTarget } from '../get-schedule-frequency-day-target';
import { getSortedTasks } from '../get-sorted-tasks';
import { getTaskDayCompletionPercentage } from '../get-task-day-completion-percentage';
import { getScheduleByDate } from '../schedules';

export type BuildCompletedDateTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  fromDate?: Date;
  rootLabel: string;
};

export type CompletedDateTreeResult = {
  tree: Record<ID, TaskListTreeItem>;
  completionData: Record<string, PrecomputedCompletion>;
};

export const buildCompletedDateTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  fromDate,
  rootLabel,
}: BuildCompletedDateTreeOptions): CompletedDateTreeResult => {
  const fromDayStart = fromDate ? startOfDay(fromDate) : undefined;

  const occurrences: {
    taskId: ID;
    dateKey: string;
    date: Timestamp;
    completedAt: Timestamp;
  }[] = [];

  tasks.forEach((task) => {
    if (task.completedAt) {
      const completedDay = startOfDay(task.completedAt);
      if (fromDayStart && completedDay < fromDayStart) {
        return;
      }
      occurrences.push({
        taskId: task.id,
        dateKey: formatDateKey(task.completedAt),
        date: completedDay,
        completedAt: task.completedAt,
      });
      return;
    }

    if (!task.schedules?.length) {
      return;
    }

    const completionsByDate = new Map<string, Timestamp>();

    task.schedules.forEach((schedule) => {
      schedule.entries.forEach((entry) => {
        if (entry.state !== ScheduleEntryState.Complete || !entry.value) {
          return;
        }
        if (fromDayStart && startOfDay(entry.date) < fromDayStart) {
          return;
        }
        const dateKey = formatDateKey(entry.date);
        const prev = completionsByDate.get(dateKey);
        if (!prev || entry.date > prev) {
          completionsByDate.set(dateKey, entry.date);
        }
      });
    });

    completionsByDate.forEach((completedAt, dateKey) => {
      const date = startOfDay(parseDateKey(dateKey));
      occurrences.push({
        taskId: task.id,
        dateKey,
        date,
        completedAt,
      });
    });
  });

  const taskTypeMap = new Map<ID, TaskType>(tasks.map((t) => [t.id, t.type]));

  const dateSections: Record<
    string,
    { date: Timestamp; taskIds: ID[]; completionTimes: Map<ID, Timestamp> }
  > = {};
  occurrences.forEach((occ) => {
    if (!dateSections[occ.dateKey]) {
      dateSections[occ.dateKey] = {
        date: occ.date,
        taskIds: [],
        completionTimes: new Map(),
      };
    }
    if (!dateSections[occ.dateKey].taskIds.includes(occ.taskId)) {
      dateSections[occ.dateKey].taskIds.push(occ.taskId);
    }
    dateSections[occ.dateKey].completionTimes.set(occ.taskId, occ.completedAt);
  });

  const dateTreeItems: Record<ID, TaskListTreeItem> = {};

  const sortedDateKeys = Object.keys(dateSections).sort(
    (a, b) => dateSections[b].date.getTime() - dateSections[a].date.getTime(),
  );

  sortedDateKeys.forEach((dateKey) => {
    dateTreeItems[dateKey] = {
      id: dateKey,
      type: TaskListItemTypes.Date,
      name: dateKey,
      children: [],
      isFolder: true,
    };
  });

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? Object.keys(dateTreeItems).reduce<ID[]>((acc, sectionId) => {
          const sectionOrder = customOrder?.[sectionId] ?? [];
          acc.push(...sectionOrder);
          return acc;
        }, [])
      : [];

  const completionData: Record<string, PrecomputedCompletion> = {};

  const allTreeItems = getSortedTasks(tasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.CreatedAt, sortingMode] : undefined,
  }).reduce<Record<ID, TaskListTreeItem>>((acc, task) => {
    Object.keys(dateSections).forEach((dateKey) => {
      if (dateSections[dateKey].taskIds.includes(task.id)) {
        const occurrenceId = createCompositeId(IDPrefixes.Occurrence, [
          dateKey,
          task.id,
        ]);
        if (acc[dateKey]) {
          acc[dateKey].children?.push(occurrenceId);
        }
        acc[occurrenceId] = {
          id: occurrenceId,
          name: task.name,
          type: TaskListItemTypes.Task,
        };

        const date = dateSections[dateKey].date;
        if (task.type === TaskType.Task) {
          completionData[occurrenceId] = {
            checked: TaskCheckOptions.Checked,
          };
        } else {
          const schedule = getScheduleByDate(task.schedules, {
            date,
            today,
          });
          const target = schedule?.frequency
            ? getScheduleFrequencyDayTarget(schedule)
            : schedule?.completionTarget.count;

          completionData[occurrenceId] = {
            checked: TaskCheckOptions.Checked,
            completionPercentage: getTaskDayCompletionPercentage(task, {
              day: date,
              weekStartsOn,
            }),
            target: target ?? undefined,
          };
        }
      }
    });

    return acc;
  }, dateTreeItems);

  sortedDateKeys.forEach((dateKey) => {
    const { completionTimes } = dateSections[dateKey];
    allTreeItems[dateKey].children?.sort((a, b) => {
      const taskIdA = getCompositeIdParts(a, IDPrefixes.Occurrence)?.[1] ?? a;
      const taskIdB = getCompositeIdParts(b, IDPrefixes.Occurrence)?.[1] ?? b;
      const isTaskA = taskTypeMap.get(taskIdA) === TaskType.Task ? 0 : 1;
      const isTaskB = taskTypeMap.get(taskIdB) === TaskType.Task ? 0 : 1;
      if (isTaskA !== isTaskB) {
        return isTaskA - isTaskB;
      }
      const timeA = completionTimes.get(taskIdA)?.getTime() ?? 0;
      const timeB = completionTimes.get(taskIdB)?.getTime() ?? 0;
      return timeB - timeA;
    });
  });

  return {
    tree: {
      [rootId]: {
        id: rootId,
        name: rootLabel,
        type: TaskListItemTypes.Root,
        children: sortedDateKeys,
      },
      ...allTreeItems,
    },
    completionData,
  };
};
